import CircleIcon from '@mui/icons-material/Circle';
import { FormControl, MenuItem, Select, SelectChangeEvent, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GroupSettingsContext } from "../../../contexts/GroupSettingsContext";
import { defaultStages } from "../../../shared/dashboard";
import theme from "../../../theme";
import { ColorOptions } from "../../../theme/colors";

const useStyles = makeStyles((theme) => ({
    text: {
        fontFamily: 'Lato',
        fontSize: '0.95rem',
        fontWeight: 'bold',
        color: '#94a3b8',
        paddingTop: 8,
    },
    subtext: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontStyle: 'italic',
        color: 'darkgray',
    },
    formContainer: {
        minWidth: '105px',
        padding: 0,
        border: 0,
        "& > .MuiInputBase-root":{
            height: '30px',
            width: 'auto',
            borderRadius: '24px',
            "& > .MuiSvgIcon-root": {
                width: 28,
                height: 28,
                fill: '#94a3b8',
            },
            "& fieldset": {
                border: 0,
            },
        },
    },
    menuItem: {
        width: '100%',
        fontSize: '0.9rem',
        fontWeight: 'bold',
    },
    badge: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '30px',
        width: 'auto',
        padding: '0 4px',
        borderRadius: 24,
        fontFamily: 'Inter',
        fontSize: '0.8rem',
        fontWeight: '500',
        textTransform: 'capitalize',
    },
}));

const StageSelector: React.FC<{
    defaultStage?: string,
    filled?: boolean,
    readOnly?: boolean,
    disabled?: boolean,
    onSelectStage?: (stage: string) => void,
}> = ({ defaultStage, onSelectStage }) => {
    const classes = useStyles();
    const { stages, setStages } = useContext(GroupSettingsContext);
    const [selectedStage, setSelectedStage] = useState<string>('');

    const palettes = useMemo(() => Array.from(new Set(Object.values(ColorOptions)
        .filter(color => !['neutral', 'brown', 'lime', 'pink', 'red', 'shades'].includes(color)))), []);

    const getColors = useCallback((title: string, withBorder?: boolean) => {
        const colorMap: Record<string, { bg: string; text: string; border: string }> = {
            'Pre-Seed': { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' }, // blue-50 / blue-700 / blue-200
            'Seed':     { bg: '#ECFEFF', text: '#0891B2', border: '#A5F3FC' }, // cyan-50 / cyan-600 / cyan-200
            'Series A': { bg: '#F5F3FF', text: '#7C3AED', border: '#DDD6FE' }, // purple-50 / purple-600 / purple-200
            'Series B': { bg: '#FDF2F8', text: '#BE185D', border: '#FBCFE8' }, // pink-50 / pink-700 / pink-200
            'Series C': { bg: '#F5F3FF', text: '#6D28D9', border: '#DDD6FE' }, // violet-50 / violet-700 / violet-200
          };


        const key = Object.keys(colorMap).find(
            k => k.toLowerCase() === title.toLowerCase()
        );

        if (key) {
            const { bg, text, border } = colorMap[key];

            return {
                backgroundColor: bg,
                dotColor: border,
                color: text,
                border: withBorder ? `1px solid ${border}` : 'inherit',
            };
        }

        const index = stages.findIndex(
            s => s.toLowerCase() === title.toLowerCase()
        );

        if (index !== -1 && index < palettes.length) {
            return {
                backgroundColor: theme.colors[palettes[index]][50],
                dotColor: theme.colors[palettes[index]][400],
                color: theme.colors[palettes[index]][700],
                border: withBorder
                    ? `1px solid ${theme.colors[palettes[index]][200]}`
                    : 'inherit',
            };
        }

        return {
            backgroundColor: theme.colors.neutral[100],
            dotColor: theme.colors.neutral[400],
            color: theme.colors.neutral[600],
            border: withBorder
            ? `1px solid ${theme.colors.neutral[500]}`
            : 'inherit',
        };
    }, [stages, palettes]);

    const handleChange = useCallback((event: SelectChangeEvent) => {
        const stage = event.target.value;
        setSelectedStage(stage);
        onSelectStage?.(stage);
    }, [onSelectStage]);

    useEffect(()=>{
        setStages(prev => !prev.length ? defaultStages : prev);
    // eslint-disable-next-line
    }, [defaultStages]);

    useEffect(()=>{
        if (defaultStage)
            setSelectedStage(defaultStage);
    }, [defaultStage]);

    return (<>
        <Stack direction="row" alignItems="center">
            {!selectedStage && (
                <Typography className={classes.text}>
                    {'Select stage ...'}
                </Typography>
            )}
            <FormControl className={classes.formContainer} variant="outlined"
                sx={!!selectedStage ? { "& > .MuiInputBase-root": { ...getColors(selectedStage, true) } } : { minWidth: '0 !important'}}>
                <Select value={selectedStage}
                    onChange={handleChange}
                    renderValue={(selected: string) => (
                        <Typography className={classes.badge}> {selected} </Typography>
                    )} MenuProps={{ PaperProps: { sx: { borderRadius: 2 }} } }
                    displayEmpty={!!selectedStage}>
                    {stages.map((stage, i) => (
                        <MenuItem className={classes.menuItem}
                            value={stage}
                            key={'stage-selector-102-' + i}>
                            <CircleIcon sx={{ width: 12, height: 12, color: getColors(stage)?.dotColor || '#000' }} />
                            <Typography className={classes.badge} ml={1}> {stage} </Typography>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Stack>
    </>);
}

export default StageSelector;